import { TrendingUp, TrendingDown, MoveRight } from "lucide-react";
import { cn } from "../../../utils/css";
import { useLegacyNavigate } from "../../../hooks/useLegacyNavigate";
import { useNavigate } from "../../../hooks/useNavigate";
import { usePermissions } from "../../../hooks/usePermissions";
import { StaffRole } from "../../../types/roles";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@pushpress/pushpress-ui";

interface MetricCardProps {
  title: string;
  currentValue: number;
  previousValue: number;
  isLegacyPage?: boolean;
  unit?: string;
  reportUrl?: string;
  reportMinRole?: StaffRole;
  isLoading?: boolean;
  dateRangeLabel?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  currentValue,
  previousValue,
  reportUrl,
  reportMinRole,
  isLoading,
  isLegacyPage,
  dateRangeLabel,
}) => {
  const { hasAtLeastRole } = usePermissions();
  const hasAccess = reportMinRole ? hasAtLeastRole(reportMinRole) : true;

  const isNoChange = currentValue === previousValue;
  const isDecreasing = currentValue < previousValue;
  const isIncreasing = currentValue > previousValue;

  // Handle percentage change calculation
  let percentageChange = 0;
  if (previousValue === 0) {
    // If previous value is 0, and current value is greater than 0, show 100% increase
    percentageChange = currentValue > 0 ? 100 : 0;
  } else {
    percentageChange = ((currentValue - previousValue) / previousValue) * 100;
  }

  // Tooltip explanation text depends on date range preset or custom selection
  const getTooltipText = () => {
    if (dateRangeLabel) {
      // For preset ranges like "Month to Date", "Last 7 Days", etc.
      if (dateRangeLabel === "Today") {
        return "Compared to yesterday";
      } else if (dateRangeLabel === "Yesterday") {
        return "Compared to the day before yesterday";
      } else if (dateRangeLabel.includes("to Date")) {
        // Handle Week to Date, Month to Date, etc.
        const periodType = dateRangeLabel.split(" to ")[0].toLowerCase();
        return `Compared to previous ${periodType} to date`;
      } else if (dateRangeLabel.includes("Last")) {
        // convert to "Compared to previous 7 day period"
        const periodType = dateRangeLabel.replace("Last", "Previous");
        return `Compared to ${periodType.toLowerCase()}`;
      }
    }

    // Default for custom date ranges
    return "Compared to previous period of equal length";
  };

  const legacyNavigate = useLegacyNavigate();
  const navigate = useNavigate();

  return (
    <button
      type="button"
      className={cn(
        "tw-w-full tw-text-left tw-bg-white tw-rounded-lg tw-shadow-sm tw-p-4 tw-border tw-border-gray-200",
        !isLoading && "hover:tw-bg-gray-50",
        "tw-transition-shadow hover:tw-shadow-none tw-ease-in-out tw-duration-300 hover:tw-outline hover:tw-outline-gray-100",
        !hasAccess && "hover:tw-cursor-not-allowed",
      )}
      onClick={() => {
        if (!isLoading && hasAccess) {
          if (isLegacyPage) {
            legacyNavigate(reportUrl);
          } else {
            navigate(reportUrl);
          }
        }
      }}
      disabled={isLoading}
    >
      <div className="tw-flex tw-justify-between tw-items-start">
        <div>
          <h3 className="tw-text-gray-600 tw-text-sm tw-font-medium">
            {title}
          </h3>
          {isLoading ? (
            <div className="tw-mt-1 tw-h-8 tw-w-16 tw-bg-gray-200 tw-rounded tw-animate-pulse" />
          ) : (
            <div className="tw-mt-1 tw-text-2xl tw-font-bold">
              {currentValue}
            </div>
          )}
        </div>
        {isLoading ? (
          <div className="tw-h-8 tw-w-20 tw-bg-gray-200 tw-rounded tw-animate-pulse" />
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "tw-flex tw-text-xs tw-font-bold tw-items-center tw-gap-1 tw-py-0.5 tw-px-2 tw-rounded-full tw-cursor-help",
                  isIncreasing && "tw-text-green-500 tw-bg-green-100",
                  isDecreasing && "tw-text-red-500 tw-bg-red-100",
                  isNoChange && "tw-text-gray-500 tw-bg-gray-100",
                )}
              >
                <span className="tw-text-md">
                  {Math.abs(percentageChange).toFixed(0)}%
                </span>
                {isNoChange && <MoveRight className="tw-w-4 tw-h-4" />}
                {isIncreasing && <TrendingUp className="tw-w-4 tw-h-4" />}
                {isDecreasing && <TrendingDown className="tw-w-4 tw-h-4" />}
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="tw-text-xs tw-bg-white !tw-text-black"
            >
              {getTooltipText()}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </button>
  );
};

export default MetricCard;
